import { AttributeKind, ItemCategory } from '@prisma/client';
import {
  CatalogProduct,
  CatalogVariant,
  VocabularyAttribute,
} from '../../types/catalog';

export const TYPE_ATTRIBUTE_KEY = 'type';
/** Brand tile key for products without a brand. */
export const NO_BRAND_ID = '__no_brand__';

export interface PickerSelection {
  category: ItemCategory | null;
  brandId: string | null;
  typeLabel: string | null;
  /** attribute key → selected value labels */
  attributeValues: Record<string, string[]>;
}

export const EMPTY_SELECTION: PickerSelection = {
  category: null,
  brandId: null,
  typeLabel: null,
  attributeValues: {},
};

export interface VariantRow {
  product: CatalogProduct;
  variant: CatalogVariant;
}

export interface FacetTile {
  key: string;
  label: string;
  imageUrl: string | null;
  count: number;
}

export interface ChipValue {
  label: string;
  count: number;
  selected: boolean;
}

export interface FilterChip {
  key: string;
  label: string;
  values: ChipValue[];
}

export interface ProductInView {
  product: CatalogProduct;
  variants: CatalogVariant[];
}

type Skip = { brand?: boolean; type?: boolean; attributeKey?: string };

export function flattenCatalog(catalog: CatalogProduct[]): VariantRow[] {
  return catalog.flatMap(product =>
    product.variants.map(variant => ({ product, variant })),
  );
}

function brandKey(product: CatalogProduct) {
  return product.brand?.id ?? NO_BRAND_ID;
}

function typeLabelOf(variant: CatalogVariant) {
  return variant.attributes[TYPE_ATTRIBUTE_KEY]?.label ?? null;
}

export function rowMatches(
  { product, variant }: VariantRow,
  selection: PickerSelection,
  skip: Skip = {},
): boolean {
  if (selection.category && product.category !== selection.category) {
    return false;
  }
  if (
    !skip.brand &&
    selection.brandId &&
    brandKey(product) !== selection.brandId
  ) {
    return false;
  }
  if (
    !skip.type &&
    selection.typeLabel &&
    typeLabelOf(variant) !== selection.typeLabel
  ) {
    return false;
  }
  return Object.entries(selection.attributeValues).every(([key, labels]) => {
    if (key === skip.attributeKey || !labels.length) return true;
    const label = variant.attributes[key]?.label;
    return !!label && labels.includes(label);
  });
}

function countProducts(
  rows: VariantRow[],
  keyOf: (row: VariantRow) => string | null,
) {
  const products = new Map<string, Set<string>>();
  rows.forEach(row => {
    const key = keyOf(row);
    if (key === null) return;
    const set = products.get(key) ?? new Set<string>();
    set.add(row.product.id);
    products.set(key, set);
  });
  return products;
}

export function categoryTiles(catalog: CatalogProduct[]): FacetTile[] {
  const counts = countProducts(
    flattenCatalog(catalog),
    r => r.product.category,
  );
  return Object.values(ItemCategory)
    .filter(category => counts.has(category))
    .map(category => ({
      key: category,
      label: category,
      imageUrl:
        catalog.find(p => p.category === category && p.coverImageUrl)
          ?.coverImageUrl ?? null,
      count: counts.get(category)!.size,
    }));
}

export function brandTiles(
  rows: VariantRow[],
  selection: PickerSelection,
): FacetTile[] {
  const inView = rows.filter(r => rowMatches(r, selection, { brand: true }));
  const counts = countProducts(inView, r => brandKey(r.product));
  const brands = new Map<string, FacetTile>();
  inView.forEach(row => {
    const key = brandKey(row.product);
    if (brands.has(key)) return;
    brands.set(key, {
      key,
      label: row.product.brand?.name ?? 'No brand',
      imageUrl: row.product.brand?.logoUrl ?? null,
      count: counts.get(key)!.size,
    });
  });
  return [...brands.values()].sort((a, b) =>
    a.key === NO_BRAND_ID
      ? 1
      : b.key === NO_BRAND_ID
      ? -1
      : a.label.localeCompare(b.label),
  );
}

export function typeTiles(
  rows: VariantRow[],
  selection: PickerSelection,
): FacetTile[] {
  const inView = rows.filter(r => rowMatches(r, selection, { type: true }));
  const counts = countProducts(inView, r => typeLabelOf(r.variant));
  return [...counts.entries()]
    .map(([label, products]) => ({
      key: label,
      label,
      imageUrl:
        inView.find(r => typeLabelOf(r.variant) === label && r.variant.imageUrl)
          ?.variant.imageUrl ?? null,
      count: products.size,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function orderedAttributes(attributes: VocabularyAttribute[]) {
  return [...attributes].sort(
    (a, b) =>
      (a.navPriority ?? Number.MAX_SAFE_INTEGER) -
        (b.navPriority ?? Number.MAX_SAFE_INTEGER) ||
      a.key.localeCompare(b.key),
  );
}

function valueSorter(attribute: VocabularyAttribute | undefined) {
  const sortOrder = new Map(
    attribute?.values.map(v => [v.label.toLowerCase(), v.sortOrder]) ?? [],
  );
  return (
    a: { label: string; numericValue: number | null },
    b: { label: string; numericValue: number | null },
  ) => {
    if (
      attribute?.kind === AttributeKind.NUMERIC &&
      a.numericValue !== null &&
      b.numericValue !== null
    ) {
      return a.numericValue - b.numericValue;
    }
    const sa = sortOrder.get(a.label.toLowerCase());
    const sb = sortOrder.get(b.label.toLowerCase());
    if (sa !== undefined && sb !== undefined && sa !== sb) return sa - sb;
    if (sa !== undefined && sb === undefined) return -1;
    if (sa === undefined && sb !== undefined) return 1;
    return a.label.localeCompare(b.label);
  };
}

/** One chip per attribute that still varies among the variants in view. */
export function buildFilterChips(
  rows: VariantRow[],
  attributes: VocabularyAttribute[],
  selection: PickerSelection,
): FilterChip[] {
  return orderedAttributes(attributes).flatMap((attribute): FilterChip[] => {
    if (attribute.key === TYPE_ATTRIBUTE_KEY) return [];
    const inView = rows.filter(r =>
      rowMatches(r, selection, { attributeKey: attribute.key }),
    );
    const counts = countProducts(
      inView,
      r => r.variant.attributes[attribute.key]?.label ?? null,
    );
    if (counts.size < 2) return [];

    const selected = selection.attributeValues[attribute.key] ?? [];
    const numericByLabel = new Map(
      inView
        .map(r => r.variant.attributes[attribute.key])
        .filter(Boolean)
        .map(v => [v.label, v.numericValue] as const),
    );
    const values = [...counts.entries()]
      .map(([label, products]) => ({
        label,
        numericValue: numericByLabel.get(label) ?? null,
        count: products.size,
        selected: selected.includes(label),
      }))
      .sort(valueSorter(attribute))
      .map(({ label, count, selected: isSelected }) => ({
        label,
        count,
        selected: isSelected,
      }));
    return [{ key: attribute.key, label: attribute.label, values }];
  });
}

export function productsInView(
  rows: VariantRow[],
  selection: PickerSelection,
): ProductInView[] {
  const byProduct = new Map<string, ProductInView>();
  rows.forEach(row => {
    if (!rowMatches(row, selection)) return;
    const entry = byProduct.get(row.product.id) ?? {
      product: row.product,
      variants: [],
    };
    entry.variants.push(row.variant);
    byProduct.set(row.product.id, entry);
  });
  return [...byProduct.values()];
}

/** Grid order: by each attribute in nav order, numeric where the kind is numeric. */
export function sortVariants(
  variants: CatalogVariant[],
  attributes: VocabularyAttribute[],
): CatalogVariant[] {
  const ordered = orderedAttributes(attributes).filter(a =>
    variants.some(v => v.attributes[a.key]),
  );
  const sorters = ordered.map(a => ({ key: a.key, compare: valueSorter(a) }));
  const compareOn = (
    a: CatalogVariant,
    b: CatalogVariant,
    { key, compare }: (typeof sorters)[number],
  ) => {
    const va = a.attributes[key];
    const vb = b.attributes[key];
    if (va && vb) return compare(va, vb);
    if (va || vb) return va ? -1 : 1;
    return 0;
  };
  return [...variants].sort(
    (a, b) =>
      sorters.reduce((acc, sorter) => acc || compareOn(a, b, sorter), 0) ||
      (a.variantLabel ?? '').localeCompare(b.variantLabel ?? ''),
  );
}

export function priceRangeLabel(
  variants: CatalogVariant[],
  format: (amount: number) => string,
) {
  if (!variants.length) return '';
  const prices = variants.map(v => v.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? format(min) : `from ${format(min)}`;
}

export function variantDisplayName(
  product: CatalogProduct,
  variant: CatalogVariant,
) {
  return variant.variantLabel
    ? `${product.name} - ${variant.variantLabel}`
    : product.name;
}
